import { GENERAL_SR_TYPE_CODE, normalizeStoredSrTypeCode } from "./checklistSrCategoryUtils";

export const normalizeChecklistQuestionLabel = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const isComplaintQuantityLabel = (questionText) => {
  const core = normalizeChecklistQuestionLabel(questionText).replace(/^\d+\s+/, "");
  return (
    core.includes("quantity under complaint") ||
    core === "complaint quantity" ||
    core.startsWith("complaint quantity ") ||
    core.includes("complaint defective quantity") ||
    core === "complaint defective quantity"
  );
};

export const isTotalQuantityPurchasedLabel = (questionText) => {
  const core = normalizeChecklistQuestionLabel(questionText).replace(/^\d+\s+/, "");
  if (core.includes("groups purchased")) return false;
  if (core.includes("complaint quantity") && !core.includes("purchased")) return false;
  return (
    core === "total quantity purchased" ||
    core.includes("total quantity purchased") ||
    (core.includes("total") && core.includes("purchased")) ||
    core.includes("total qty purchased")
  );
};

const rowChildSrNumber = (d) =>
  String(d?.ChildSRNumber ?? d?.childSRNumber ?? d?.ChildSrNumber ?? "").trim();

const rowQuestionText = (row, questionTextByCode) => {
  const fromRow = String(
    row?.QuestionText ?? row?.questionText ?? row?.question ?? ""
  ).trim();
  if (fromRow) return fromRow;
  const code = String(
    row?.QuestionCODE ??
      row?.QuestionCode ??
      row?.questionCODE ??
      row?.questionCode ??
      ""
  ).trim();
  if (!code || !questionTextByCode) return "";
  return String(questionTextByCode.get(code) ?? questionTextByCode.get(code.toLowerCase()) ?? "").trim();
};

export const filterMaterialRowsForChecklistQty = (
  details,
  { childSrCode = null, srTypeCode = null } = {}
) => {
  const childFilter = childSrCode ? String(childSrCode).trim() : "";
  const typeFilter =
    srTypeCode && String(srTypeCode).trim() !== GENERAL_SR_TYPE_CODE
      ? String(srTypeCode).trim()
      : "";

  const selected = (details || []).filter(
    (d) => d?.IsSelected !== 0 && d?.isSelected !== 0
  );
  const anyHasChild = selected.some((d) => rowChildSrNumber(d));

  return selected.filter((d) => {
    if (childFilter && anyHasChild && rowChildSrNumber(d) !== childFilter) {
      return false;
    }
    if (typeFilter) {
      const code = String(d?.SRTypeCode ?? d?.srTypeCode ?? d?.SRTYPECODE ?? "").trim();
      if (code !== typeFilter) return false;
    }
    return true;
  });
};

/** SR qty summed; invoice qty once per ProductCode (clone/+ rows). */
export const computeMaterialSectionTotals = (details) => {
  const seenBaseProducts = new Set();
  let totalInvoiceQty = 0;
  let totalSrQty = 0;
  (details || []).forEach((d) => {
    if (d?.IsSelected === 0 || d?.isSelected === 0) return;
    totalSrQty +=
      parseFloat(d.SRQuantity ?? d.srQuantity ?? d.ComplaintQty ?? d.complaintQty ?? 0) || 0;
    const baseCode = String(d.ProductCode ?? d.productCode ?? "").trim();
    if (baseCode && seenBaseProducts.has(baseCode)) return;
    if (baseCode) seenBaseProducts.add(baseCode);
    totalInvoiceQty +=
      parseFloat(
        d.InvoiceQuantity ?? d.invoiceQuantity ?? d.Quantity ?? d.quantity ?? 0
      ) || 0;
  });
  return {
    totalInvoiceQty: Math.floor(totalInvoiceQty),
    totalSrQty: Math.floor(totalSrQty),
  };
};

const qtyValueForQuestion = (questionText, totals) => {
  if (!totals) return null;
  if (isComplaintQuantityLabel(questionText) && totals.totalSrQty != null) {
    return String(totals.totalSrQty);
  }
  if (isTotalQuantityPurchasedLabel(questionText) && totals.totalInvoiceQty != null) {
    return String(totals.totalInvoiceQty);
  }
  return null;
};

/**
 * When viewing a child SR, checklist Complaint Quantity / Total quantity purchased
 * must show that child's SR qty and invoice qty, not the parent totals saved on raise.
 */
export const applyChildMaterialQtyToChecklistResponses = (
  rows,
  materialRows,
  childSrCode,
  questionTextByCode = null
) => {
  const childFilter = childSrCode ? String(childSrCode).trim() : "";
  if (!childFilter || !Array.isArray(rows) || rows.length === 0) return rows;

  const childLines = filterMaterialRowsForChecklistQty(materialRows, {
    childSrCode: childFilter,
  });
  if (childLines.length === 0) return rows;

  const allChildTotals = computeMaterialSectionTotals(childLines);
  const totalsBySr = new Map();

  return rows.map((row) => {
    const text = rowQuestionText(row, questionTextByCode);
    const derivedAll = qtyValueForQuestion(text, allChildTotals);
    if (derivedAll == null) return row;

    const sr = normalizeStoredSrTypeCode(row?.SRTypeCode ?? row?.srTypeCode);
    let totals = allChildTotals;
    if (sr !== GENERAL_SR_TYPE_CODE) {
      if (!totalsBySr.has(sr)) {
        const catLines = filterMaterialRowsForChecklistQty(materialRows, {
          childSrCode: childFilter,
          srTypeCode: sr,
        });
        totalsBySr.set(sr, {
          totals: computeMaterialSectionTotals(catLines),
          hasLines: catLines.length > 0,
        });
      }
      const cat = totalsBySr.get(sr);
      if (cat?.hasLines) totals = cat.totals;
    }

    const derived = qtyValueForQuestion(text, totals);
    if (derived == null) return row;
    const num = Number(derived);
    return {
      ...row,
      QuestionText: text || row.QuestionText,
      questionText: text || row.questionText,
      ResponseText: derived,
      responseText: derived,
      response: derived,
      ResponseValue: Number.isFinite(num) ? num : row.ResponseValue,
      responseValue: Number.isFinite(num) ? num : row.responseValue,
    };
  });
};
